/**
 * Utility methods for components to manipulate and navigate the DOM across the app.
 *
 * @module lib/DOMUtils
 */

interface ViewportDimensions {
  height: number;
  width: number;
}
/**
 * Finds the ancestor of an element until it returns an ancestor with a matching class.
 *
 * @param {object} element - a given DOM element.
 * @param {string} className class name - a given class to match against DOM elements.
 * @returns {object} - ancestor with matching class.
 */
export function findAncestor(
  element: HTMLElement,
  className: string | -1,
): HTMLElement | null | undefined {
  let ancestor: HTMLElement | null | undefined = element;
  if (className === -1) {
    do {
      ancestor = ancestor?.parentElement;
    } while (!(ancestor?.tabIndex === className));
  } else {
    do {
      ancestor = ancestor?.parentElement;
    } while (!ancestor?.classList.contains(className));
  }
  return ancestor;
}

export function setFocusWithinList(
  list: HTMLElement,
  itemClassName: string,
  direction: 'first' | 'previous' | 'next' | '',
): void {
  const currentFocusTarget =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const parentRow =
    direction !== 'first'
      ? currentFocusTarget && !currentFocusTarget.classList.contains(itemClassName)
        ? findAncestor(currentFocusTarget, itemClassName)
        : currentFocusTarget
      : '';
  switch (direction) {
    case 'first': {
      // Focus on the first element in this list.
      const firstList = list ? list.querySelector('.' + itemClassName) : '';
      if (firstList instanceof HTMLElement) {
        firstList.focus();
      }
      break;
    }
    case 'previous': {
      // Set focus on previous list item.
      const prevSearchResult = parentRow instanceof HTMLElement ? parentRow?.previousSibling : null;
      // @ts-expect-error TS(2339) FIXME: Property 'classList' does not exist on type 'Child... Remove this comment to see the full error message
      if (prevSearchResult && prevSearchResult.classList.contains(itemClassName)) {
        // @ts-expect-error TS(2339) FIXME: Property 'focus' does not exist on type 'ChildNode... Remove this comment to see the full error message
        prevSearchResult.focus();
      }
      break;
    }
    case 'next': {
      // Set focus on next list item.
      // @ts-expect-error TS(2339) FIXME: Property 'nextSibling' does not exist on type 'HTM... Remove this comment to see the full error message
      const nextSearchResult = parentRow?.nextSibling;
      if (nextSearchResult && nextSearchResult.classList.contains(itemClassName)) {
        nextSearchResult.focus();
      }
      break;
    }
  }
}

export function getViewportDimensions(): ViewportDimensions {
  return {
    height: window.innerHeight || document.documentElement.clientHeight,
    width: window.innerWidth || document.documentElement.clientWidth,
  };
}
